const defaultNS = '' as const;

const resources = {
  en: {
    users: {
      loading: 'Loading users...',
      title: 'Users',
      refetching: 'Refetching users...',
      refetch_success: 'Users updated!',
      refetch_error: 'Failed to update users.',
      refetch_tooltip: 'Click here to refetch users.',
      super_admin_column_header: 'Super Admin',
      grant_super_admin_title: 'Grant Super Admin?',
      grant_super_admin_description:
        'Grant {{name}} Super Admin access across the entire portal? They will have full platform-wide administrative privileges.',
      revoke_super_admin_title: 'Revoke Super Admin?',
      revoke_super_admin_description:
        'Revoke Super Admin access from {{name}}? They will lose platform-wide administrative privileges across the portal.',
      grant_super_admin_loading: 'Granting Super Admin to {{name}}...',
      grant_super_admin_success: '{{name}} has been granted Super Admin access.',
      grant_super_admin_error: 'Failed to grant Super Admin to {{name}}.',
      revoke_super_admin_loading: 'Revoking Super Admin from {{name}}...',
      revoke_super_admin_success: 'Super Admin access revoked from {{name}}.',
      revoke_super_admin_error: 'Failed to revoke Super Admin from {{name}}.',
      super_admin_self_tooltip: 'You cannot change your own Super Admin status.',
      grant_super_admin_tooltip: 'Grant Super Admin access',
      revoke_super_admin_tooltip: 'Revoke Super Admin access',
      global_table_title: 'All Users (Super Admin View)',
      global_table_subtitle: 'Viewing all users across the portal. Use filters to narrow results.',
      is_super_admin_column_header: 'Platform Super Admin',
      is_super_admin_yes: 'Super Admin',
      is_super_admin_no: 'Standard',
      updated_at_column_header: 'Updated At',
      last_login_at_column_header: 'Last Login',
      can_create_studies_column_header: 'Create Studies',
      can_create_runs_column_header: 'Create Runs',
      organization_column_header: 'Organization',
      // Users list additions (pluralization keys use _one/_other suffixes)
      user_count_one: '{{count}} User',
      user_count_other: '{{count}} Users',
      download_list: 'Download List',
      download_failed: 'Could not download the user list. Please try again.',
      // User detail additions
      detail_loading: 'Loading user...',
      detail_not_found: 'User not found.',
      detail_load_error: 'Could not load this user. Please try again.',
      primary_role: 'Primary Role',
      status: 'Status',
      status_active: 'Active',
      status_inactive: 'Inactive',
      org_memberships: 'Organization Memberships',
      primary_role_indicator: '(Primary Role)',
      study_count_one: '{{count}} Study',
      study_count_other: '{{count}} Studies',
      study_load_error: "Could not load this user's studies. Please try again.",
      // Studies table column headers (UserStudyPublic shape)
      study_col_name: 'Study Name',
      study_col_organization: 'Organization',
      study_col_contact: 'Contact',
      study_col_permissions: 'Permissions',
      study_col_created_at: 'Created At',
      study_col_created_by: 'Created By',
    },
    auth: {
      saml: {
        provider_name_required: 'Provider name is required',
        provider_name_too_long: 'Provider name must be 32 characters or fewer',
        email_domain_required: 'Email domain is required',
        attr_email_required: 'Attribute email is required',
        attr_first_name_required: 'Attribute first name is required',
        attr_last_name_required: 'Attribute last name is required',
        attr_roles_required: 'Attribute roles is required',
        metadata_required: 'Either Metadata URL or Metadata File is required',
        metadata_url_invalid: 'Enter a valid URL',
        metadata_file_rejected: 'That file could not be used. Select an XML metadata file.',
        metadata_exclusive: 'Provide either a Metadata URL or a Metadata File, not both.',
        portal_parameters: 'Portal SAML Parameters',
        portal_parameters_tooltip: 'Portal SAML Parameters further information.',
        recipient: 'Recipient',
        audience: 'Audience',
        configuring: 'Configuring...',
        configure_provider: 'Configure SAML Provider',
        updating_config: 'Updating authentication configuration...',
        config_success: 'SAML provider configured successfully!',
        config_error: 'Failed to configure SAML provider',
        loading_settings: 'Loading Authentication Settings...',
        error_loading_settings: 'Error loading authentication settings.',
        saml_tab: 'SAML',
        identity_provider: 'SAML Identity Provider',
        identity_provider_tooltip: 'SAML Identity Provider further information.',
        provider_name_label: 'Provider Name',
        email_domain_label: 'Email Domain',
        metadata_url_label: 'Metadata URL',
        or: 'or',
        selected_metadata_file: 'Selected: {{file}}',
        existing_metadata_file: 'Using the saved metadata file — upload a new one to replace it',
        upload_metadata_xml: 'Upload Metadata XML',
        user_attribute_mapping: 'User Attribute Mapping',
        user_attribute_mapping_tooltip: 'User Attribute Mapping further information.',
        attr_email_label: 'Attribute Email',
        attr_first_name_label: 'Attribute First Name',
        attr_last_name_label: 'Attribute Last Name',
        attr_roles_label: 'Attribute Roles',
        aria_tablist_auth_settings: 'Authentication settings',
        aria_portal_parameters: 'Portal SAML Parameters',
        aria_identity_provider: 'SAML Identity Provider',
        aria_user_attribute_mapping: 'User Attribute Mapping',
        refresh_metadata: 'Refresh Metadata',
        refresh_provider: 'Refresh Provider',
        refreshing_metadata: 'Refreshing...',
        refreshing_provider: 'Refreshing...',
        clear_file: 'Clear',
        please_fix_errors: 'Please fix the following errors:',
        options_tooltip: 'Click here to reveal data refresh options.',
      },
    },
    create: {
      errors: {
        noStudiesAvailable: 'No studies available. Please create a study first before creating an instrument run.',
      },
      run: {
        studyId: 'Study ID',
        orgId: 'Org ID',
        addDescription: 'Add Description',
        clickToCreateStudy: 'Click here to create a new study.',
        created: 'has been created successfully!',
        submitted: 'has been submitted successfully!',
        creating: 'Creating Run...',
        failed: 'Failed to create instrument run. Please try again.',
        noOrgSelected: 'No organization selected',
        noStudySelected: 'Please select a study',
        demuxIndex: 'Demux',
        orgName: 'Organization Name',
        sampleNum: 'Sample #',
        studyName: 'Study Name',
        wellPosition: 'Well Pos',
        saveAsDraft: 'Save',
        savedAsDraft: 'has been saved as a draft!',
        updated: 'has been updated successfully!',
        failedToSaveDraft: 'Failed to save draft. Please try again.',
        editsNotSavedNonDraft:
          'Only consumable Lot IDs and serial numbers can be edited once a run leaves Draft — your other changes were not saved.',
        error: {
          // Backend 422: the names collide with samples that ALREADY EXIST server-side.
          sampleConflictsBackend_one: '1 sample name already exists in your organization — see the highlighted row',
          sampleConflictsBackend_other:
            '{{count}} sample names already exist in your organization — see highlighted rows',
          // Backend 422: generic per-sample validation errors (not name conflicts) — the exact
          // reason is shown in each highlighted row's tooltip.
          sampleErrorsBackend_one: '1 sample has errors — see the highlighted row',
          sampleErrorsBackend_other: '{{count}} samples have errors — see highlighted rows',
          // Client-side: the names are duplicated WITHIN this run's sample table.
          sampleConflictsInRun_one: '1 sample name is duplicated in this run — see the highlighted row',
          sampleConflictsInRun_other: '{{count}} sample names are duplicated in this run — see highlighted rows',
          // Client-side: two rows claim the same plate coordinate (well + demux), which the
          // backend rejects at draft-create.
          sampleWellConflictsInRun_one: '1 sample shares a well and demux index with another — see the highlighted row',
          sampleWellConflictsInRun_other:
            '{{count}} samples share a well and demux index with another — see highlighted rows',
        },
        validation: {
          nameRequired: 'Name is required',
          nameInvalidChars: 'Name cannot contain apostrophes or backticks',
          numberOfSamplesRequired: 'Number of samples is required',
          sampleNameRequired: 'Sample name is required',
          studyNameRequired: 'Study name is required',
          orgRequired: 'Organization is required',
          wellPositionRequired: 'Well position is required',
          uploadedSamplesMismatch: 'Number of uploaded samples must match the expected number of samples',
          allSampleFieldsRequired: 'All sample fields must be filled with valid values',
          sampleNameConflict: 'One or more sample names already exist in their respective studies',
          duplicateInRun: 'Duplicate sample name in this run',
          duplicateWellInRun: 'Duplicate well position and demux index in this run',
          libraryGroupRequired: 'Library group is required.',
          libraryGroupTooLong: 'Library group must be {{max}} characters or fewer.',
          libraryGroupNotActive: 'This library group is not on the plate for the current control count.',
          libraryGroupUnused: "No samples are mapped to {{groups}} — that control's QC won't cover any sample.",
          alreadyInOrg: 'Already exists in your organization',
        },
      },
      experiment: {
        fields: {
          assayType: {
            label: 'Assay Type',
            options: {
              alpha: 'Alpha Synuclein',
              broadscale: 'Broadscale',
              tau: 'Tau',
            },
          },
          assayVersion: {
            label: 'Assay Version',
            options: {
              naut: 'Tau',
              broadscale: 'Broadscale',
            },
          },
          description: 'Instrument Run Description',
          lotNumber: 'Prep Kit Lot Number',
          name: 'Instrument Run Name',
          noOfSamples: {
            label: 'Number of Samples',
            options: {
              four: '4',
              twelve: '12',
              twentyfour: '24',
            },
          },
          species: {
            label: 'Species',
            options: {
              human: 'Human',
              mouse: 'Mouse',
            },
          },
          study: {
            label: 'Study',
            options: {
              lecanemab: 'Lecanemab',
              lungs: 'Lungs',
              mj: 'MJ Fox Study',
            },
          },
        },
      },
      study: {
        create: 'Create Study',
        creating: 'Creating Study...',
        failed: 'Failed to create study. Please try again.',
        success: 'study has been created successfully!',
        team_members: 'Team Members',
        add_member_placeholder: 'Search by name...',
        no_members_hint: 'No members added. The creator is automatically added as owner.',
        remove_user: 'Remove {{name}}',
      },
    },
    errors: {
      copySuccess: 'Error copied to clipboard.',
      copyTooltip: 'Click here to copy the error message.',
      levels: {
        err: 'Error',
        warn: 'Warning',
      },
    },
    experiments: {
      name: 'Experiments',
      new: 'Create New Experiment',
      cols: {
        code: 'Code',
        dateComplete: 'Completed On',
        dateSubmit: 'Submitted On',
        name: 'Experiment Name',
        instrument: 'Instrument',
        noOfSamples: 'Samples',
        status: 'Status',
        study: 'Study',
        submitBy: 'Submitted By',
        type: 'Type',
      },
    },
    general: {
      actions: 'Actions',
      add_user: 'Add User',
      edit: 'Edit',
      adding: 'Adding...',
      cancel: 'Cancel',
      code: 'Code',
      copy_question: 'Copy?',
      confirm: 'Confirm',
      copied: 'Copied: ',
      failedCopy: 'Failed to copy',
      begin_typing_here: 'Begin typing here...',
      created: 'Created',
      search_user: 'Add User',
      created_at: 'Created at',
      created_by: 'Created by',
      current_users: 'Current Users',
      desc: 'Description',
      download_csv: 'Download CSV',
      email: 'Email',
      empty: '-',
      failed: 'Failed',
      no: 'No',
      is_admin: 'Make Admin',
      name: 'Name',
      logging_in: 'Logging in...',
      remove: 'Remove',
      removed: 'removed successfully',
      removing: 'Removing...',
      roles: 'Roles',
      save: 'Save',
      selected: 'Selected',
      settings: 'Settings',
      success: 'Success',
      date_time_format: 'Date & time format',
      applies_across_app: 'Applies across the app',
      date_format_description: 'Choose how dates and times appear in tables, activity feeds, and dashboards.',
      date_format_options: 'Date format options',
      choose_date_time_format: 'Choose date and time format',
      date_format_updated: 'Date format updated to {{format}}',
      date_format_presets: {
        short: {
          label: 'Short',
          description: 'Compact numeric month/day/year with time',
        },
        medium: {
          label: 'Medium',
          description: 'Readable month abbreviation with day, year, and time',
        },
        long: {
          label: 'Long',
          description: 'Detailed weekday, month, day, year, and time',
        },
      },
      updated: 'Modified',
      updated_at: 'Modified at',
      updated_by: 'Modified by',
      user_id: 'User ID',
      yes: 'Yes',
      first_name: 'First Name',
      last_name: 'Last Name',
      phone: 'Phone',
      validation_email_required: 'Email is required.',
      validation_email_invalid: 'Please enter a valid email address.',
      validation_first_name_required: 'First name is required.',
      validation_last_name_required: 'Last name is required.',
      reset: 'Reset',
      sort: {
        asc: 'Sort ascending',
        clear: 'Clear sort',
        desc: 'Sort descending',
      },
      submit: 'Submit',
      submitting: 'Submitting',
      view: {
        card: 'card',
        card_v: 'View Cards',
        table: 'table',
        table_v: 'View Table',
      },
      user_management: {
        owner: 'Owner',
        allowed_users: 'Allowed Users',
        promote_title: 'Promote to Owner?',
        promote_description:
          'This will give {{name}} full control over this study, including the ability to manage other users.',
        promote_tooltip: 'Promote user to owner',
        revoke_title: 'Revoke Access?',
        revoke_description: '{{name}} will no longer be able to access this study.',
        revoke_owner_note: 'Note: This will demote them from owner status.',
        revoke_tooltip: "Revoke user's access",
        select_user_error: 'Please select a user',
        adding_user: 'Adding {{name}}...',
        adding: 'Adding user...',
        added_success: '{{name}} was granted access successfully',
        added_success_generic: 'User access granted successfully',
        added_error: 'Failed to grant access to {{name}}: {{error}}',
        added_error_generic: 'Failed to grant access: {{error}}',
        revoking_user: 'Revoking access for {{name}}...',
        revoking: 'Revoking access...',
        revoked_success: "{{name}}'s access was revoked successfully",
        revoked_success_generic: 'User access revoked successfully',
        revoked_error: 'Failed to revoke access for {{name}}: {{error}}',
        revoked_error_generic: 'Failed to revoke access: {{error}}',
        promoting_user: 'Promoting {{name}} to owner...',
        promoting: 'Promoting user...',
        promoted_success: '{{name}} was promoted to owner successfully',
        promoted_success_generic: 'User promoted to owner successfully',
        promoted_error: 'Failed to promote access to {{name}}: {{error}}',
        promoted_error_generic: 'Failed to promote user: {{error}}',
        adding_user_row: '{{name}} (Adding...)',
        adding_role: '...',
      },
      unauthorized: {
        title: 'Unauthorized',
        message1: 'You do not have permission to access this page.',
        message2: 'Please contact your administrator if you believe this is an error.',
        go_home: 'Go to Studies',
        attempted_path: 'You tried to access: {{path}}',
      },
      alias: 'Alias',
      studies: 'Studies',
      primary_contact: 'Primary Contact',
      it_contact: 'IT Contact',
      copy_contact_details: 'Copy all contact details',
      copy_contact_success: 'Contact details copied!',
      copy_contact_error: 'Failed to copy contact details',
    },
    instruments: {
      title: 'Instruments',
      fetching: 'Fetching instruments...',
      refetching: 'Refreshing instruments...',
      refetch_success: 'Instruments updated!',
      refetch_error: 'Failed to refresh instruments.',
      refetch_tooltip: 'Refresh instruments',
      create_instrument: 'Add Instrument',
      create_title: 'Add Instrument',
      edit_title: 'Edit Instrument',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      save_error: 'Failed to save instrument. Please try again.',
      create_saving: 'Adding instrument...',
      create_success: 'Instrument "{{name}}" added successfully.',
      create_error: 'Failed to add instrument. Please try again.',
      update_saving: 'Updating instrument...',
      update_success: 'Instrument "{{name}}" updated successfully.',
      update_error: 'Failed to update instrument. Please try again.',
      create_error_detail: 'Failed to add instrument: {{error}}',
      update_error_detail: 'Failed to update instrument: {{error}}',
      delete_confirm: 'Are you sure you want to delete instrument "{{name}}"?',
      delete_confirm_short: 'Delete?',
      delete_dialog_title: 'Delete "{{name}}"?',
      delete_dialog_description: 'Are you sure you want to permanently delete "{{name}}"?',
      delete_dialog_sub_description: 'This action cannot be undone.',
      delete_deleting: 'Deleting "{{name}}"...',
      delete_yes: 'Yes, Delete',
      delete_success: 'Instrument "{{name}}" deleted successfully.',
      delete_error: 'Failed to delete "{{name}}": {{error}}',
      edit_tooltip: 'Edit instrument',
      delete_tooltip: 'Delete instrument',
      field_name: 'Name',
      field_name_placeholder: 'e.g. Nemo A',
      field_serial_num: 'Serial Number',
      field_serial_num_placeholder: 'e.g. SN-00123',
      field_model: 'Model',
      field_model_placeholder: 'e.g. Nemo',
      field_organization: 'Organization',
      field_is_available: 'Available',
      field_next_pm_at: 'Next Preventative Maintenance',
      validation_name_required: 'Instrument name is required.',
      validation_org_required: 'Please select an organization',
      col_serial_num: 'Serial Number',
      col_model: 'Model',
      col_is_available: 'Available',
      col_next_pm_at: 'Next PM',
      available: 'Available',
      unavailable: 'Unavailable',
      empty_state: 'No instruments found. Create one to get started.',
      empty_state_read_only: 'No instruments found.',
    },
    login: {
      graphic: {
        heading: 'Welcome to the Proteome Analysis Cloud Portal',
        subtitle:
          'Accelerate your proteomics research with scalable, high-throughput protein identification and quantification.',
        image_alt: 'Nautilus Proteome Analysis Platform',
      },
      buttons: {
        login: 'Login',
        loginsso: 'Login with SSO',
        logout: 'Logout',
        use_password: 'I have a password...',
        resend_code: 'Resend Code',
      },
      logging_out: 'Logging out...',
      form: {
        heading: 'Login to the Portal',
        description: 'Sign in to access your Nautilus account.',
        errors: {
          email: {
            required: 'An email is required.',
            short: 'An email must be at least {{min}} characters long.',
          },
          password: {
            required: 'A password is required.',
            short: 'Your password must be at least {{min}} characters long.',
          },
          reset_password_failed: 'Failed to reset password. You will be automatically redirected to the login page.',
          reset_password_weak: "That password doesn't meet the password requirements. Try a different one.",
          reset_password_too_many: 'Too many attempts. Wait a little, then try again — your reset link still works.',
          reset_password_link_invalid:
            'This link is no longer valid. You will be automatically redirected to the login page.',
        },
        labels: {
          email: 'E-Mail',
          email_required: 'Email is required.',
          invalid_email: 'Invalid email address.',
          pass: 'Password',
          pass_required: 'A password is required.',
          passTooltip: 'Click to toggle text visibility.',
          valid_email_required: 'You must enter a valid email address.',
          reset: 'Reset',
          submit: 'Submit',
          validating: 'Validating',
          forgot_password: 'Forgot password?',
          forgot_password_instructions:
            'Enter your email address and we will send you instructions to reset your password.',
          send_reset_link: 'Send Reset Link',
          invalid_reset_link: 'Invalid Reset Link',
          invalid_reset_link_message_1: 'This password reset link is invalid or incomplete.',
          invalid_reset_link_message_2: 'This can happen if the link was copied incorrectly or has expired.',
          request_new_reset_link: 'Request New Reset Link',
          return_to_login: 'Return to Login',
          forgot_password_success: 'If your email is registered, a reset link was sent to {{email}}.',
          forgot_password_error: 'Failed to send reset link. Please try again.',
          reset_password: 'Reset Password',
          reset_password_instructions: 'Enter the reset token you received and your new password.',
          reset_password_success: 'Your password has been reset successfully.',
          reset_password_error: 'Failed to reset password. Please try again.',
          token: 'Reset Token',
          new_password: 'New Password',
          sending: 'Sending...',
          resetting_password: 'Resetting password...',
        },
      },
      loading: {
        default: 'Fetching latest data',
        verifying_session: 'Verifying your session',
      },
      session_check_failed: {
        title: "We couldn't verify your session",
        message: "We couldn't confirm you're signed in. Try again, or return to the login page.",
        retry: 'Try Again',
        return_to_login: 'Return to Login',
      },
      auto_redirect: 'Redirecting in {{count}}s...',
    },
    logo: { alt: 'Nautilus Biotechnology Portal' },
    noOrg: {
      title: 'Welcome to Nautilus Biotechnology Portal.',
      message: 'Please contact your admin to add you to an organization.',
    },
    orgs: {
      fetching: 'Fetching Organizations',
      id: 'ID',
      name: 'Organizations',
      current_organization: 'Selected Organization: {{name}}',
      manage_users_title: 'Manage Users - {{name}}',
      manage_users_tooltip: 'Click here to manage users in this organization.',
      user_added_success: 'User added successfully to {{orgName}}',
      user_added_failed: 'Failed to add user to {{orgName}}',
      user_id_description: 'Enter the UUID of the user to add to this organization',
      user_not_found: 'User not found with that ID',
      org_not_found: 'Organization not found',
      user_permissions: 'User Permissions',
      search_user_placeholder: 'Type valid email here...',
      search_user_description: "Add a valid email above and select the role(s) you'd like applied to this user.",
      please_select_user: 'Please select a user from the search results',
      no_users_in_org: 'No users have been added to this organization yet.',
      confirm_remove_user: 'Are you sure you want to remove {{name}} from this organization?',
      user_removed_success: '{{name}} removed successfully from {{orgName}}',
      user_removed_failed: 'Failed to remove user from {{orgName}}',
      user_search_failed: 'Failed to search users. Please try again.',
      user_search_invalid_response: 'Received invalid data from user search. Please try again.',
      org_switched_success: 'Switched to {{name}}',
      org_switched_save_error: 'Switched to {{name}}, but the selection could not be saved.',
      select_organization: 'Select {{name}} organization',
      view_studies_one: 'View {{count}} study for {{name}}',
      view_studies_other: 'View {{count}} studies for {{name}}',
      refetching: 'Refetching organizations...',
      refetch_success: 'Organizations updated!',
      refetch_error: 'Error loading organization: {{message}}',
      list_refetch_error: 'Failed to update organizations.',
      refetch_tooltip: 'Click here to refetch organizations.',
      email_invalid: 'Please enter a valid email.',
      select_at_least_one_role: 'Select at least one role.',
      updating_roles: 'Updating roles...',
      roles_updated: 'Roles updated for {{userName}}',
      removing_roles: 'Removing roles...',
      roles_removed: 'Roles removed for {{userName}}',
      failed_update_roles: 'Failed to update roles for {{userName}}',
      edit_roles: 'Edit roles',
      roles_selector: 'roles selector',
      save_roles: 'Save roles.',
      cancel_role_editing: 'Cancel role editing.',
      org_selector_organizations: 'Organizations',
      org_selector_current: 'Current organization',
      org_loading: 'Loading Organizations...',
      org_selector_role_switch_success: 'Switched to {{role}}',
      org_selector_role_switch_failed: 'Failed to switch role',
      org_selector_role_switch_nav_failed: 'Switched role, but the page could not be reloaded. Please refresh.',
      org_selector_role_switch_stale_grids_one:
        'Switched to {{role}}, but {{count}} table could not be refreshed and may still show the previous role. Please refresh the page.',
      org_selector_role_switch_stale_grids_other:
        'Switched to {{role}}, but {{count}} tables could not be refreshed and may still show the previous role. Please refresh the page.',
      org_selector_no_roles: 'No roles available',
      org_selector_search_placeholder: 'Search organizations...',
      org_selector_no_results: 'No organizations found.',
      create_org: 'Create Organization',
      create_org_title: 'Create New Organization',
      create_org_name_label: 'Organization Name *',
      create_org_name_placeholder: 'Enter organization name',
      create_org_creating: 'Creating {{name}}.',
      create_org_submit: 'Create Organization',
      create_org_success: 'Organization created successfully',
      create_org_error: 'Failed to create organization',
      create_org_cancel: 'Cancel',
      create_org_section_basic: 'Basic Information',
      create_org_section_primary_contact: 'Primary Contact',
      create_org_section_it_contact: 'IT Contact',
      // Domain is a single-field section whose heading doubles as the field's only visible label,
      // so the required marker goes here rather than on create_org_domain_label, which nothing renders.
      create_org_section_domain: 'Domain *',
      create_org_section_subscription: 'Subscription',
      create_org_address_label: 'Address *',
      create_org_address_placeholder: 'Street address, City, State, ZIP',
      create_org_alias_label: 'Alias *',
      create_org_alias_placeholder: 'e.g. ACME',
      create_org_first_name_label: 'First Name *',
      create_org_first_name_placeholder: 'Enter first name',
      create_org_last_name_label: 'Last Name *',
      create_org_last_name_placeholder: 'Enter last name',
      create_org_phone_label: 'Phone',
      create_org_phone_placeholder: '+1 555 000 0000',
      create_org_email_label: 'Email *',
      create_org_email_placeholder: 'name@example.com',
      create_org_domain_label: 'Domain',
      create_org_domain_placeholder: 'example.com',
      create_org_subscription_active_label: 'Active',
      create_org_subscription_expiration_label: 'Expiration Date',
      edit_org_back: 'Back',
      edit_org_edit_tooltip: 'Edit organization details',
      edit_org_read_only:
        'You have view-only access to this organization. Editing organization details requires a platform super admin.',
      edit_org_save_all: 'Save All',
      validating: 'Validating',
      validation_name_required: 'Organization name is required.',
      validation_name_min_length: 'Organization name must be at least 3 characters long.',
      validation_name_max_length: 'Organization name must be 100 characters or fewer.',
      validation_address_required: 'Address is required.',
      validation_address_max_length: 'Address must be 500 characters or fewer.',
      validation_alias_required: 'Alias is required.',
      validation_alias_min_length: 'Alias must be at least 2 characters long.',
      validation_alias_max_length: 'Alias must be 50 characters or fewer.',
      validation_alias_invalid: 'Alias can only contain letters, numbers, and hyphens.',
      validation_alias_duplicate:
        'This alias is already in use by another organization. Please choose a different one.',
      validation_first_name_required: 'First name is required.',
      validation_last_name_required: 'Last name is required.',
      validation_person_name_max_length: 'Must be 50 characters or fewer.',
      validation_person_name_invalid: 'Can only contain letters, spaces, hyphens, and apostrophes.',
      validation_phone_invalid: 'Enter a valid phone number.',
      validation_email_required: 'Email is required.',
      validation_email_invalid: 'Enter a valid email address.',
      validation_domain_required: 'Domain is required.',
      validation_domain_invalid: 'Enter a valid domain.',
      validation_expiration_required: 'Expiration date is required when subscription is active.',
      validation_expiration_past: 'Expiration date cannot be in the past.',
      create_org_error_detail: 'Failed to create organization: {{message}}',
      edit_org_saving: 'Saving organization...',
      edit_org_success: 'Organization saved successfully',
      edit_org_error: 'Failed to save organization',
      edit_org_error_detail: 'Failed to save organization: {{message}}',
      download_list: 'Download List',
      download_failed: 'Could not download the organizations list. Please try again.',
    },
    // Keys are the lower_snake wire values from `ROLES` (@/types) — see useRoleLabel.
    roles: {
      super_user: 'Super Admin',
      org_admin: 'Org Admin',
      data_owner: 'Data Owner',
    },
    runs: {
      create_new_run: 'Create New Run',
      create_study: 'Create Study',
      download_failed: 'Failed to download instrument runs',
      run_count_one: '{{count}} Run',
      run_count_other: '{{count}} Runs',
      run_count_loading: '— Runs',
      run_count_error: 'Run count unavailable',
      header: {
        name: 'Name',
        description: 'Description',
        edit: 'Edit',
        type: 'Type',
        species: 'Species',
        instrument_id: 'Instrument ID',
        id_label: 'ID: {{id}}',
        description_label: 'Description:',
        description_aria: 'Edit instrument run description',
        tooltip_status_info: 'Click here to reveal status update information.',
        created: 'Created',
        updated: 'Updated',
        submitted: 'Submitted',
        completed: 'Completed',
        samples: 'Samples',
        // Assay is an FK to a catalog record now; the picker shows assay_friendly_name.
        assay: 'Assay',
        consumables: 'Consumables',
        runs_label: 'Runs',
        pin_to_start_run: 'Pin to Start Run',
        instrument: 'Instrument',
        modified: 'Modified',
        started: 'Started',
        finished: 'Finished',
        submitted_by: 'Submitted By',
        updated_by: 'Updated By',
        instrument_status: 'Instrument Status',
        decode_status: 'Decode Status',
        run_qc: 'Run QC',
        sample_qc: 'Sample QC',
        run_time: 'Run Time',
        controls: 'Controls',
        col_flowcell: 'Flowcell',
        col_lane: 'Lane',
        col_well: 'Well',
        col_demux_id: 'Demux ID',
        col_sample: 'Sample',
        col_library_group: 'Library Group',
        col_sample_quality_qc: 'Sample Quality QC',
        col_decode_qc: 'Decode QC',
        col_lane_qc: 'Lane QC',
        col_controls_qc: 'Controls QC',
        col_warning: 'Warning',
        col_warning_yes: 'Yes',
        col_warning_no: 'No',
        col_sample_group: 'Sample Group',
        col_study: 'Study',
        col_organization: 'Organization',
        cons_flowcell_1: 'Flowcell 1 SN',
        cons_flowcell_2: 'Flowcell 2 SN',
        cons_flowcell_3: 'Flowcell 3 SN',
        cons_sample_plate: 'Sample Plate SN',
        cons_detection_plate: 'Detection Plate SN',
        cons_system_reagents: 'System Reagents SN',
        cons_run_buffer: 'Run Buffer SN',
        cons_system_clean: 'System Clean SN',
        cons_input_additive_a: 'Run Buffer Additive A Lot ID',
        cons_input_additive_b: 'Run Buffer Additive B Lot ID',
        cons_input_additive_c: 'Run Buffer Additive C Lot ID',
        cons_input_lib_prep_a: 'Library Prep Kit A SN',
        cons_input_lib_prep_b: 'Library Prep Kit B SN',
        cons_input_lib_prep_c: 'Library Prep Kit C SN',
        cons_input_lib_prep_controls: 'Library Prep Kit Controls SN',
        cons_input_max_length: 'Must be {{count}} characters or fewer.',
        cons_input_lot_id_placeholder: 'Enter Lot ID',
        cons_input_sn_placeholder: 'Enter Serial Number',
        cons_input_save: 'Save',
        cons_input_save_success: 'Consumables saved.',
        cons_input_save_error: 'Could not save consumables. Please try again.',
        run_complete: 'Run complete.',
      },
      sampleDetails: {
        title: 'Sample Details',
        download: 'Download',
        download_failed: 'Could not download the sample list. Please try again.',
        stat_proteoforms: 'Proteoforms',
        stat_ptms: 'PTMs',
        stat_isoforms: 'Isoforms',
        stat_threshold: 'Threshold',
        stat_threshold_value: '{{value}} cpm',
        col_flowcell: 'Flowcell',
        col_lane: 'Lane',
        col_well: 'Well',
        col_demux_id: 'Demux ID',
        col_sample: 'Sample',
        col_sample_groups: 'Sample Groups',
        col_proteoform_group: 'Proteoform Group',
        col_abundance: 'Abundance (cpm)',
        col_n_terminus: 'N Terminus',
        col_0n: '0N',
        col_1n: '1N',
        col_2n: '2N',
        col_3r: '3R',
        col_4r: '4R',
        col_c_terminus: 'C Terminus',
        col_pt181: 'pT181',
        col_ps202_pt205: 'pS202+PT205',
        col_pt205: 'pT205',
        col_ps214: 'pS214',
        col_pt217: 'pT217',
        col_pt231: 'pT231',
        col_ps396: 'pS396',
      },
      fetching: 'Fetching Instrument Runs data',
      new_run: 'New Run',
      edit_run: 'Edit Run',
      controls: {
        title: 'Controls',
        flowcell: 'Flow Cell',
        lane: 'Lane',
        well: 'Well',
        demux_id: 'Demux ID',
        sample_name: 'Sample Name',
        type: 'Type',
        study: 'Study',
        organization: 'Organization',
        library_group: 'Library Group',
        sample_group: 'Sample Group',
        useA4Control: 'Using A4 library prep control',
        libraryGroupCount: 'Number of library prep controls used',
        libraryGroupCountLocked_one: 'Only {{count}} library group is available for this sample count',
        libraryGroupCountLocked_other: 'Only {{count}} library groups are available for this sample count',
      },
      no_runs: 'No runs found',
      no_runs_p: 'Click the "Create New Run" button to get started.',
      no_studies_message: 'You need to create a study first before creating instrument runs.',
      run: {
        back: 'Back to Instrument Runs',
        fetching: 'Fetching Run data',
      },
      loading: 'Loading',
    },
    sampleManager: {
      study_select_placeholder: 'Search for a study...',
      study_select_dialog_title: 'Select a Study',
      study_select_empty_hint: 'Click to select a study',
      table_description: 'Double click a cell to edit it.',
      flow_cell: 'Flow Cell',
      lane: 'Lane',
      library_group: 'Library Group',
      sample_group: 'Sample Group',
      study_apply_to_all_empty: 'Apply to all empty rows',
      study_copy_to_rows: 'Apply to rows',
    },
    sidebar: {
      assume_role: 'Assume Role',
      aut: 'Authentication',
      exp: 'Experiments',
      fle: 'Fleet',
      help: 'Help',
      instr: 'Instruments',
      org: 'Organizations',
      profile: 'Profile',
      profile_email: 'Email',
      profile_name: 'Name',
      profile_no_role: 'No role assigned',
      profile_role: 'Role in {{org}}',
      role_none: 'No role assigned',
      role_only: 'Your only role in this organization: {{role}}',
      role_select: 'Select role',
      role_switcher: 'Switch role',
      roles: 'Roles',
      runs: 'Runs',
      sam: 'Samples',
      stu: 'Studies',
      use: 'Users',
      toggle: {
        collapse: 'Collapse sidebar',
        expand: 'Expand sidebar',
      },
      user_menu: 'Account, settings & logout',
    },
    studies: {
      card_view: 'Studies card view',
      fetching: 'Fetching Study data',
      goToInstrumentRun: 'Go to Instrument Run',
      goToSample: 'Go to Sample',
      goToStudy: 'Go to Study',
      last_updated: 'Last updated: {{date}}',
      loading: 'Loading latest studies data',
      no_studies: 'No studies found',
      no_studies_p: 'Click the "Create New Study" button to get started.',
      refetch_error: 'Failed to refresh studies data.',
      refetch_success: 'Studies data successfully refreshed!',
      refetch_tooltip: 'Refresh Data',
      refetching: 'Refetching studies...',
      title: 'Samples',
      study: {
        back: 'Back to Studies',
        home: 'Studies',
        manage_users: 'Manage Users',
        edit: 'Edit',
        edit_study: 'Edit Study',
        edit_study_title: 'Edit Study',
        edit_success: '{{name}} updated successfully.',
        edit_failed: 'Failed to update study. Please try again.',
        edit_saving: 'Saving study...',
        download: 'Download Study',
        edit_name_aria: 'Edit Study Name',
        edit_description_aria: 'Edit Study Description',
        no_org_selected: 'No organization selected',
        organization: 'Organization',
        contact: 'Contact',
        no_email: 'No email.',
        sample_groups: 'Sample Groups',
        samples_label: 'Total Samples',
        instrument_runs_label: 'Runs',
        broadscale_samples: 'Broadscale Samples',
        tau_samples: 'Tau Samples',
        studySamplesTitle: 'Study Samples',
        studySamplesTotal: 'Total',
        studySamplesPassedQc: 'Passed QC',
        studySamplesDownloadFailed: 'Could not download the sample list. Please try again.',
        assaySamplesDownloadFailed: 'Could not download the results table. Please try again.',
        assayDetailLink: 'Details',
        broadscaleDetailTitle: 'Broadscale v1 Samples',
        tauDetailTitle: 'Tau v1 Samples',
        tauSummary: {
          download: 'Download',
          downloadFailed: 'Could not download the results table. Please try again.',
          totalSamples: 'Total Samples',
          proteoforms: 'Proteoforms',
          ptms: 'PTMs',
          isoforms: 'Isoforms',
          threshold: 'Threshold',
          thresholdValue: '{{value}} cpm',
          columnConfigUnavailable:
            "This assay's column layout couldn't be loaded, so the samples table is unavailable. Reload the page to try again.",
        },
        proteinsQuantified: 'Proteins Quantified',
        validation: {
          name_required: 'Name is required.',
          name_min_length: 'Name must be at least 3 characters long.',
          name_no_apostrophe: "Name cannot contain apostrophes (').",
        },
      },
      table_view: 'Studies table view',
      page_title: 'Studies',
      study_count_one: '{{count}} Study',
      study_count_other: '{{count}} Studies',
      download_list: 'Download List',
      download_failed: 'Could not download the study list. Please try again.',
      samples_list_download_failed: 'Could not download the sample list. Please try again.',
      warning_yes: 'Yes',
      warning_no: 'No',
      sample_count_one: '{{count}} Sample',
      sample_count_other: '{{count}} Samples',
      sample_count_loading: '— Samples',
      stat_studies: 'Studies',
      stat_passed_qc: 'Passed QC',
      stat_sample_groups: 'Sample Groups',
      columnHeaders: {
        sampleNum: 'Sample #',
        sampleName: 'Sample Name',
        study: 'Study',
        contact: 'Contact',
        description: 'Description',
        numSamples: 'Samples',
        organization: 'Organization',
        instrumentRun: 'Run',
        wellPosition: 'Well Position',
        demuxIndex: 'Demux',
        proteoform: 'Proteoform',
        proteoformCount: 'Proteoform Count',
        abundance: 'Abundance',
        createdAt: 'Created At',
        sampleGroup: 'Sample Group',
        sampleGroups: 'Sample Groups',
        decodeQc: 'Decode QC',
        studyName: 'Study Name',
        runName: 'Run Name',
        assay: 'Assay',
        species: 'Species',
        created: 'Created',
        updated: 'Updated',
        createdBy: 'Created By',
        updatedBy: 'Updated By',
        proteoformGroup: 'Proteoform Group',
        quantityCpm: 'Quantity (cpm)',
        abundanceCpm: 'Abundance (cpm)',
        isControl: 'Is Control',
        protein: 'Protein',
        sample: 'Sample',
        well: 'Well',
        demuxId: 'Demux ID',
        sampleQualityQc: 'Sample Quality QC',
        laneQc: 'Lane QC',
        controlsQc: 'Controls QC',
        // The collapsed single-column view of the four QC category columns.
        sampleQc: 'Sample QC',
        warning: 'Warning',
        flowcell: 'Flowcell',
        lane: 'Lane',
        libraryGroup: 'Library Group',
        instrument: 'Instrument',
      },
      proteoformCounts: {
        title: 'Sample Proteoform Counts',
        noData: 'No proteoform count data available',
      },
      proteoforms: {
        loading: 'Loading proteoforms for this sample...',
        title: 'Sample Proteoforms',
        noData: 'No proteoform data available',
        error: 'Failed to load proteoforms for this sample.',
        name: 'Proteoform Name',
        abundance: 'Abundance',
        wellPosition: 'Well Position',
        createdAt: 'Created At',
        fetchingSampleData: 'Fetching sample data',
        tableAriaLabel: 'Proteoforms for sample',
      },
      samples: {
        collapseDetail: 'Collapse details for {{name}}',
        expandDetail: 'Expand details for {{name}}',
        details: {
          backToSamples: 'Back to Samples',
          detailsHeading: 'Details',
          additionalDataHeading: 'Additional Data',
          fieldOrganisation: 'Organisation',
          fieldStudy: 'Study',
          fieldInstrumentRun: 'Instrument Run',
          fieldWellPosition: 'Well Position',
          fieldDemuxIndex: 'Demux Index',
          fieldCreatedAt: 'Created At',
          fieldUpdatedAt: 'Updated At',
          notFound: 'Sample not found.',
          loadFailed: 'Something went wrong loading this sample. Please try again.',
          loadFailedPermanent:
            "This sample's data could not be read. This looks like a server problem — please contact support.",
          additionalDataPlaceholder: 'Coming soon.',
          // --- Sample detail redesign ---
          runHeading: 'Run',
          userMetadataHeading: 'User Metadata',
          proteoformAbundancesHeading: 'Proteoform Abundances',
          fieldSpecies: 'Species',
          fieldSampleGroup: 'Sample Group',
          fieldModified: 'Modified',
          fieldCreated: 'Created',
          fieldStarted: 'Started',
          fieldFinished: 'Finished',
          fieldName: 'Name',
          qcSampleQuality: 'Sample Quality QC',
          qcDecode: 'Decode QC',
          qcLane: 'Lane QC',
          qcControls: 'Controls QC',
          // Replaced the old free-text message panel; `message` was dropped as a dead column.
          qcOverall: 'Overall QC',
          actionEdit: 'Edit',
          actionDownload: 'Download Sample',
          actionComingSoon: 'Coming soon',
          userMetaGender: 'Gender',
          userMetaHeight: 'Height (in)',
          userMetaIris: 'Iris Color',
          threshold: 'Threshold',
          thresholdValue: '{{value}} cpm',
          download: 'Download',
          downloadFailed: 'Could not download. Please try again.',
          statProteoforms: 'Proteoforms',
          statPtms: 'PTMs',
          statIsoforms: 'Isoforms',
          matrixAriaLabel: 'Proteoform abundances for sample',
        },
      },
    },
  },
};

const ns = Object.keys(resources.en);

export { defaultNS, ns, resources };
